import { useEffect, useState, type FormEvent } from 'react';
import { useCopy, useT } from '@/i18n';
import { Nav } from '@/marketing/blocks/nav';
import { Footer } from '@/marketing/blocks/footer';
import { Section } from '@/marketing/blocks/Section';
import en from '@/i18n/locales/en.json';
import {
  eventHomepageContentSchema,
  readEventHomepageContent,
  type EventHomepageContent,
} from '@/marketing/content/event-homepage-schema';

function track(name: string) {
  void fetch('/api/marketing/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, dimensions: { marketingVariant: 'event-home' } }),
    keepalive: true,
  }).catch(() => {});
}

function useEventHomepageContent(): EventHomepageContent {
  const copy = useCopy();
  try {
    return readEventHomepageContent(copy);
  } catch (error) {
    if (import.meta.env.DEV) throw new Error(`Invalid event_home localization: ${String(error)}`);
    console.warn('i18n.event_home_fallback', { reason: 'schema_mismatch' });
    return eventHomepageContentSchema.parse(en.event_home);
  }
}

function BetaSignup({ copy }: { copy: EventHomepageContent['beta'] }) {
  const [email, setEmail] = useState('');
  const [eventInterest, setEventInterest] = useState('Morocco 2030');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!consent) return;
    track('event_beta_signup_started');
    const response = await fetch('/api/event-beta-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, eventInterest, consent }),
    });
    if (response.ok) {
      setStatus('sent');
      track('event_beta_signup_completed');
    } else setStatus('error');
  }
  return (
    <Section id="event-beta" bg="sand">
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <span className="eyebrow">{copy.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14 }}>
          {copy.title}
        </h2>
        <p className="lead" style={{ marginTop: 16 }}>
          {copy.lead}
        </p>
        {status === 'sent' ? (
          <p role="status" style={{ marginTop: 20 }}>
            {copy.sent}
          </p>
        ) : (
          <form onSubmit={submit} style={{ display: 'grid', gap: 14, marginTop: 24 }}>
            <label>
              {copy.emailLabel}
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label>
              {copy.eventInterestLabel}
              <input
                required
                value={eventInterest}
                onChange={(e) => setEventInterest(e.target.value)}
              />
            </label>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span>{copy.consent}</span>
            </label>
            <button className="btn btn-primary" type="submit" disabled={!consent}>
              {copy.submit}
            </button>
            {status === 'error' ? <p role="alert">{copy.error}</p> : null}
          </form>
        )}
      </div>
    </Section>
  );
}

export default function EventHome() {
  const { direction } = useT();
  const copy = useEventHomepageContent();
  useEffect(() => {
    document.title = copy.seo.title;
    document
      .querySelector<HTMLMetaElement>('meta[name="description"]')
      ?.setAttribute('content', copy.seo.description);
    document.documentElement.dir = direction;
  }, [copy.seo.description, copy.seo.title, direction]);
  return (
    <>
      <Nav ctaPrimary={copy.navCta} />
      <Section id="hero" bg="paper">
        <span className="eyebrow">{copy.hero.eyebrow}</span>
        <h1 className="h-display" style={{ marginTop: 20, maxWidth: 760 }}>
          {copy.hero.title}
        </h1>
        <p className="lead" style={{ marginTop: 24, maxWidth: 660 }}>
          {copy.hero.body}
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 30 }}>
          <a
            className="btn btn-primary"
            href="#event-beta"
            onClick={() => track('event_pack_cta_clicked')}
          >
            {copy.hero.primaryCta}
          </a>
          <a className="btn btn-ghost" href="#offline-proof">
            {copy.hero.secondaryCta}
          </a>
        </div>
        <p style={{ marginTop: 24, color: 'var(--muted)' }}>{copy.hero.strapline}</p>
      </Section>
      <Section id="offline-proof" bg="ink">
        <div style={{ color: 'var(--paper)', maxWidth: 780 }}>
          <span className="eyebrow">{copy.offlineProof.eyebrow}</span>
          <h2 className="h-section" style={{ marginTop: 14, color: 'var(--paper)' }}>
            {copy.offlineProof.title}
          </h2>
          <ol style={{ display: 'grid', gap: 12, marginTop: 24, paddingInlineStart: 24 }}>
            {copy.offlineProof.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      </Section>
      <Section id="event-packs" bg="paper-2">
        <span className="eyebrow">{copy.pack.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14, maxWidth: 760 }}>
          {copy.pack.title}
        </h2>
        <p className="lead" style={{ marginTop: 18, maxWidth: 760 }}>
          {copy.pack.body}
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            gap: 14,
            marginTop: 28,
          }}
        >
          {copy.pack.steps.map((step, i) => (
            <article
              key={step}
              style={{ border: '1px solid var(--line)', borderRadius: 14, padding: 18 }}
            >
              <strong>
                {i + 1}. {step}
              </strong>
            </article>
          ))}
        </div>
      </Section>
      <Section id="today" bg="paper">
        <span className="eyebrow">{copy.today.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14 }}>
          {copy.today.title}
        </h2>
        <div style={{ display: 'grid', gap: 12, marginTop: 24, maxWidth: 760 }}>
          {copy.today.items.map((item) => (
            <article
              key={item.time}
              style={{
                display: 'grid',
                gridTemplateColumns: '80px 1fr',
                gap: 12,
                borderBottom: '1px solid var(--line)',
                padding: '14px 0',
              }}
            >
              <strong>{item.time}</strong>
              <div>
                <strong>{item.title}</strong>
                <p style={{ color: 'var(--muted)', marginTop: 5 }}>
                  {copy.today.missionPrefix}: {item.mission}
                </p>
              </div>
            </article>
          ))}
        </div>
      </Section>
      <Section id="capabilities" bg="paper-2">
        <span className="eyebrow">{copy.capabilities.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14 }}>
          {copy.capabilities.title}
        </h2>
        <div style={{ overflowX: 'auto', marginTop: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'start', padding: 10 }}>
                  {copy.capabilities.columns.capability}
                </th>
                <th style={{ textAlign: 'start', padding: 10 }}>
                  {copy.capabilities.columns.offline}
                </th>
                <th style={{ textAlign: 'start', padding: 10 }}>
                  {copy.capabilities.columns.connected}
                </th>
              </tr>
            </thead>
            <tbody>
              {copy.capabilities.rows.map((row) => (
                <tr key={row.id} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: 10 }}>
                    <strong>{row.label}</strong>
                    <div style={{ color: 'var(--muted)', fontSize: 13 }}>{row.description}</div>
                  </td>
                  <td style={{ padding: 10 }}>{row.offline}</td>
                  <td style={{ padding: 10 }}>{row.connected}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section id="morocco-pilot" bg="sand">
        <span className="eyebrow">{copy.pilot.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14 }}>
          {copy.pilot.title}
        </h2>
        <p className="lead" style={{ marginTop: 18, maxWidth: 720 }}>
          {copy.pilot.body}
        </p>
        <p style={{ marginTop: 18 }}>{copy.pilot.status}</p>
      </Section>
      <Section id="comparison" bg="paper">
        <span className="eyebrow">{copy.comparison.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14 }}>
          {copy.comparison.title}
        </h2>
        <div style={{ overflowX: 'auto', marginTop: 24 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'start', padding: 10 }}>{copy.comparison.columns.need}</th>
                <th style={{ textAlign: 'start', padding: 10 }}>
                  {copy.comparison.columns.whatimean}
                </th>
                <th style={{ textAlign: 'start', padding: 10 }}>
                  {copy.comparison.columns.notWhatimean}
                </th>
              </tr>
            </thead>
            <tbody>
              {copy.comparison.rows.map((row) => (
                <tr key={row.need} style={{ borderTop: '1px solid var(--line)' }}>
                  <td style={{ padding: 10 }}>{row.need}</td>
                  <td style={{ padding: 10 }}>{row.whatimean}</td>
                  <td style={{ padding: 10 }}>{row.notWhatimean}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section id="pricing" bg="sand">
        <span className="eyebrow">{copy.pricing.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14 }}>
          {copy.pricing.title}
        </h2>
        <p className="lead" style={{ marginTop: 18, maxWidth: 720 }}>
          {copy.pricing.body}
        </p>
        <div
          style={{
            display: 'grid',
            gap: 14,
            gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
            marginTop: 24,
          }}
        >
          {copy.pricing.cards.map((card) => (
            <article
              key={card.title}
              style={{ border: '1px solid var(--line)', borderRadius: 14, padding: 18 }}
            >
              <strong>{card.title}</strong>
              <p style={{ marginTop: 8 }}>{card.body}</p>
            </article>
          ))}
        </div>
      </Section>
      <Section id="faq" bg="paper-2">
        <span className="eyebrow">{copy.faq.eyebrow}</span>
        <h2 className="h-section" style={{ marginTop: 14 }}>
          {copy.faq.title}
        </h2>
        <div style={{ maxWidth: 780, marginTop: 20 }}>
          {copy.faq.items.map((item) => (
            <details
              key={item.question}
              style={{ borderBottom: '1px solid var(--line)', padding: '16px 0' }}
            >
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{item.question}</summary>
              <p style={{ marginTop: 10, color: 'var(--muted)' }}>{item.answer}</p>
            </details>
          ))}
        </div>
      </Section>
      <BetaSignup copy={copy.beta} />
      <Section id="final-cta" bg="ink">
        <div style={{ textAlign: 'center', color: 'var(--paper)' }}>
          <h2 className="h-section" style={{ color: 'var(--paper)' }}>
            {copy.finalCta.title}
          </h2>
          <a className="btn btn-primary" href="#event-beta" style={{ marginTop: 24 }}>
            {copy.finalCta.cta}
          </a>
        </div>
      </Section>
      <Footer />
    </>
  );
}
